import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "ai-assistant-container" }
const _hoisted_2 = { class: "content-wrapper" }
const _hoisted_3 = {
  key: 2,
  class: "skeleton-container"
}
const _hoisted_4 = {
  key: 3,
  class: "chat-body"
}
const _hoisted_5 = { class: "container-summary" }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 4,
  class: "chat-body center"
}
const _hoisted_8 = { class: "container-summary center" }
const _hoisted_9 = ["textContent"]

import { ref, watch, inject, onMounted, computed, type Ref } from "vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";
import SummaryHeader from "./components/SummaryHeader.vue";
import SummaryContent from "./components/SummaryContent.vue";
import SummaryFooter from "./components/SummaryFooter.vue";
import AIAssistantStartTranscription from "./AIAssistantStartTranscription.vue";
import AIAssistantTranscription from "./AIAssistantTranscription.vue";

import { useCustomForms } from "@/composables/useCustomForms";
import { useSessionStore } from "@/composables/useSessionStore";
import { formatSummaryData } from "@/utils/formatters";

interface ApiClient {
  get: (url: string, params?: any) => Promise<any>;
  post: (url: string, data?: any) => Promise<any>;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'AIAssistantSummary',
  props: {
  initialContext: {
    type: Object,
    required: true,
  },
  /**
   * Parent-controlled summary status from the state machine.
   * This takes precedence over initialContext.transcriptionStatus for initial state.
   */
  parentSummaryStatus: {
    type: String,
    default: null,
  },
},
  emits: [
  "update-appointment",
  "ready-for-review",
  "review-saved",
  "session-discarded",
  "close",
  "restart-session",
  "update-status",
  "transcription-completed",
  "show-transcription-modal",
],
  setup(__props, { emit: __emit }) {

/**
 * AIAssistantSummary.vue - Summary/Review Component
 *
 * Main component for reviewing transcriptions and summaries.
 * Handles WebSocket updates for status changes and displays
 * appropriate content based on the summary status.
 */
const sessionStore = useSessionStore();
const { setRecorderSessionId, setSummary } = sessionStore;

const props = __props;

const emit = __emit;

const { t } = useI18n();

// State
const actionState = ref("summary");

// Simplified: Use parentSummaryStatus directly from props (controlled by state machine)
// Fall back to computed status from initialContext only if parentSummaryStatus not provided
const summaryStatus = computed(() => {
  if (props.parentSummaryStatus) return props.parentSummaryStatus;
  return getSummaryStatus(props.initialContext?.transcriptionStatus);
});
const initialStatus = summaryStatus.value;

// Initialize summaryText from initialContext if available (for page refresh / session recovery)
const getInitialSummaryText = (): string => {
  const ctx = props.initialContext;
  if (!ctx) return "";

  // Try to get summary data from various possible fields
  const rawSummary =
    ctx.summaryData ||
    ctx.parsedSummary ||
    ctx.reviewedSummary ||
    ctx.data?.summaryData ||
    ctx.data?.parsedSummary ||
    ctx.data?.summary ||
    ctx.summary ||
    ""; // Also check ctx.summary directly

  if (!rawSummary) {
    return "";
  }

  // Format if needed
  return formatSummaryData(rawSummary);
};

const summaryText = ref(initialStatus === "ready" ? getInitialSummaryText() : "");
const reviewStatus = ref(props.initialContext?.reviewedSummary ? "reviewed" : "pending");

// Initialize generatingTranscription based on initial status
// If we're already in processing state on mount, show the loading UI
const generatingTranscription = ref(initialStatus === "processing");
const transcriptionMessages = ref(props.initialContext?.data?.transcription || []);
const initialMessage = ref(props.initialContext?.initialMessage ? marked(props.initialContext.initialMessage) : null);
const summaryErrorMessage = ref("");
const toastsData = ref([]);

// Constants
const today = new Date().toISOString().split("T")[0];
const sessionTimestamp = props.initialContext?.timestamp?.split("T")[0] || today;

const preemiumConfig = {
  active: false,
  mode: "time",
  limit: 30,
  credits: 10,
  releaseDate: "2025-03-24",
};

const isAlfa = props.initialContext.licenseId === 17973 || props.initialContext.licenseId === 9326;

// Composables
const { customForms } = useCustomForms(props.initialContext.forms || []);

// Get transcription modal management methods from parent
const openTranscriptionModal = inject<(iframeUrl: string) => void>("openTranscriptionModal");
const closeTranscriptionModal = inject<() => void>("closeTranscriptionModal");

// Get API client
const apiClient = inject<ApiClient | null>("apiClient");

// Check if parent has WebSocket connection (AIAssistant.vue)
// If parent is connected, we don't need to connect again
const parentWsConnected = inject<Ref<boolean> | null>("wsConnected", null);

// Status change handling - simplified since parent manages WebSocket
// Local UI state (generatingTranscription) is updated, status changes emitted to parent
// Status change handling - simplified since parent manages WebSocket
// Status tracking is now handled centrally by useStatusTracking composable
const handleStatusChange = (status: string, data: any) => {
  let newSummaryStatus = summaryStatus.value;
  status = status?.toLowerCase();

  console.log("[AIAssistantSummary] handleStatusChange", status, data);

  if (status === "recording") {
    newSummaryStatus = "recording";
    generatingTranscription.value = false; // Not generating yet, still recording
  } else if (["recorded", "transcribing", "transcribed", "analyzing"].includes(status)) {
    newSummaryStatus = "processing";
    // Set generatingTranscription to true to show loading UI during processing states
    generatingTranscription.value = true;
  } else if (status === "summarized") {
    newSummaryStatus = "ready";
    transcriptionMessages.value = data.transcription;

    if (!data.reviewedSummary) {
      reviewStatus.value = "pending";
      summaryText.value = data.parsedSummary;
    } else {
      summaryText.value = data.reviewedSummary;
      reviewStatus.value = "reviewed";
    }

    generatingTranscription.value = false;

    // Close iframe modal and open transcription text modal when status is Summarized
    if (closeTranscriptionModal) {
      closeTranscriptionModal();
    }

    // Fetch transcription text from API
    const sessionId = props.initialContext?.sessionId || props.initialContext?.recorderSessionId;
    if (apiClient && sessionId) {
      apiClient
        .get(`/session/${sessionId}`)
        .then((sessionData) => {
          // Extract transcription text and summary data from session data
          const transcriptionText = sessionData?.transcription || sessionData?.data?.transcription || "";

          // Try multiple possible field names for summaryData
          const summaryData =
            sessionData?.summaryData ||
            sessionData?.data?.summaryData ||
            sessionData?.summary ||
            sessionData?.data?.summary ||
            sessionData?.parsedSummary ||
            sessionData?.data?.parsedSummary ||
            sessionData?.reviewedSummary ||
            sessionData?.data?.reviewedSummary ||
            "";

          // Set summaryText with summaryData if available
          if (summaryData) {
            const formattedSummary = formatSummaryData(summaryData);
            setRecorderSessionId(sessionId);
            setSummary(formattedSummary);
            summaryText.value = formattedSummary;
          }

          emit("show-transcription-modal", {
            transcription: transcriptionText,
            summary: data.parsedSummary || data.reviewedSummary || sessionData?.summary,
            summaryData: summaryData,
          });
        })
        .catch((error) => {
          console.error("[AIAssistantSummary] Error fetching session data:", error);
          // Fallback: use data from WebSocket
          emit("show-transcription-modal", {
            transcription: data.transcription,
            summary: data.parsedSummary || data.reviewedSummary,
            summaryData: data.parsedSummary || data.reviewedSummary || "",
          });
        });
    } else {
      // Fallback: use data from WebSocket
      emit("show-transcription-modal", {
        transcription: data.transcription,
        summary: data.parsedSummary || data.reviewedSummary,
        summaryData: data.parsedSummary || data.reviewedSummary || "",
      });
    }

    if (reviewStatus.value === "pending") {
      emit("ready-for-review");
    }
  }

  // Emit status update to parent (state machine handles actual status changes)
  if (newSummaryStatus && summaryStatus.value !== "error") {
    emit("update-status", newSummaryStatus);
  }
};

// WebSocket is managed by parent (AIAssistant.vue)
// Status changes come through parentSummaryStatus prop

onMounted(() => {
  // Component mounted - initialization complete
});

// Watch summaryStatus to sync generatingTranscription
// This ensures the processing UI shows when status changes from external sources (parent, props)
watch(
  summaryStatus,
  (newStatus) => {
    // Set generatingTranscription based on status
    if (newStatus === "processing") {
      generatingTranscription.value = true;
    } else if (newStatus === "ready" || newStatus === "error" || newStatus === "interrupted") {
      generatingTranscription.value = false;
    }
  },
  { immediate: false } // Don't run immediately since we set initial value above
);

// Watch parentSummaryStatus prop for changes from parent state machine
// This ensures the UI reflects the state machine status (e.g., "processing" when reviewStatus is "processing")
// immediate: true ensures this fires on mount if the prop has a value
watch(
  () => props.parentSummaryStatus,
  (newParentStatus) => {
    // summaryStatus is now computed from props, just update local UI state
    if (newParentStatus === "processing") {
      generatingTranscription.value = true;
    } else if (newParentStatus === "ready" || newParentStatus === "error") {
      generatingTranscription.value = false;

      // When status changes to "ready", update summaryText if we have summary data
      if (newParentStatus === "ready") {
        const newSummary = getInitialSummaryText();
        if (newSummary && !summaryText.value) {
          summaryText.value = newSummary;
        }
      }
    }
  },
  { immediate: true }
);

// Watch initialContext.summaryData for changes - this handles when summary data arrives after component mount
// This is critical for the flow: processing -> review_ready (summaryData arrives)
watch(
  () => props.initialContext?.summaryData,
  (newSummaryData, oldSummaryData) => {
    if (newSummaryData && newSummaryData !== oldSummaryData) {
      // Only update if we don't already have summary text (avoid overwriting user edits)
      if (!summaryText.value) {
        const newSummary = getInitialSummaryText();
        if (newSummary) {
          summaryText.value = newSummary;
        }
      }
    }
  },
  { immediate: true, deep: false }
);

// Watch for external status changes from props - just emit update to parent
watch(
  () => props.initialContext?.transcriptionStatus || props.initialContext?.recorderStatus,
  (newStatus) => {
    if (newStatus) {
      const mappedStatus = getSummaryStatus(newStatus);
      if (mappedStatus && mappedStatus !== summaryStatus.value) {
        emit("update-status", mappedStatus);
      }
    }
  }
);

// WebSocket is managed by parent (AIAssistant.vue)
// Status changes come through props.parentSummaryStatus

// Methods
function getSummaryStatus(status: string) {
  status = status?.toLowerCase();
  return ["recorded", "transcribing", "transcribed", "analyzing"].includes(status) ? "processing" : status;
}

const handleTabChanged = (tab: string) => {
  actionState.value = tab;
};

const onUpdateAppointment = (appointment: any) => {
  emit("update-appointment", {
    appointmentId: appointment.appointmentId,
  });
};

const onSummarySaved = () => {
  reviewStatus.value = "reviewed";
  emit("review-saved");
};

const onSummaryDenied = () => {
  emit("session-discarded");
};

const onClose = () => {
  emit("close");
};

const restartSession = () => {
  // Reset local state and emit to parent (summaryStatus controlled by props)
  actionState.value = "summary";
  generatingTranscription.value = true;
  initialMessage.value = null;
  summaryText.value = null;
  reviewStatus.value = "pending";
  summaryErrorMessage.value = "";
  toastsData.value = [];
  emit("update-status", "initialized");
  emit("restart-session", { initialContext: props.initialContext });
};

const onSummaryTextUpdate = (newValue: string) => {
  summaryText.value = newValue;
};

if (props.initialContext.data) {
  handleStatusChange(summaryStatus.value, props.initialContext.data);
}

return (_ctx: any,_cache: any) => {
  const _component_w_skeleton = _resolveComponent("w-skeleton")!
  const _component_w_button = _resolveComponent("w-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (summaryStatus.value !== 'initialized' && summaryStatus.value !== 'interrupted')
      ? (_openBlock(), _createBlock(SummaryHeader, {
          key: 0,
          "initial-context": __props.initialContext,
          "summary-status": summaryStatus.value,
          onTabChanged: handleTabChanged
        }, null, 8, ["initial-context", "summary-status"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      (
          (!summaryStatus.value ||
            summaryStatus.value == 'recording' ||
            summaryStatus.value == 'interrupted' ||
            summaryStatus.value == 'initialized' ||
            summaryStatus.value == 'processing') &&
            actionState.value !== 'context'
        )
        ? (_openBlock(), _createBlock(AIAssistantStartTranscription, {
            key: 0,
            "action-state": actionState.value,
            "summary-status": summaryStatus.value,
            "generating-transcription": generatingTranscription.value,
            "initial-context": __props.initialContext,
            "toasts-data": toastsData.value,
            onUpdateAppointment: onUpdateAppointment
          }, null, 8, ["action-state", "summary-status", "generating-transcription", "initial-context", "toasts-data"]))
        : _createCommentVNode("", true),
      (actionState.value === 'transcription' && summaryStatus.value === 'ready')
        ? (_openBlock(), _createBlock(AIAssistantTranscription, {
            key: 1,
            "initial-context": __props.initialContext,
            "transcription-messages": transcriptionMessages.value
          }, null, 8, ["initial-context", "transcription-messages"]))
        : _createCommentVNode("", true),
      (generatingTranscription.value && summaryStatus.value !== 'processing')
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_w_skeleton, {
              height: 18,
              shape: "default",
              width: 360
            }),
            _createVNode(_component_w_skeleton, {
              height: 18,
              shape: "default",
              width: 360,
              class: "skeleton-item"
            }),
            _createVNode(_component_w_skeleton, {
              height: 18,
              shape: "default",
              width: 360,
              class: "skeleton-item"
            }),
            _createVNode(_component_w_skeleton, {
              height: 18,
              shape: "default",
              width: 60,
              class: "skeleton-item"
            })
          ]))
        : _createCommentVNode("", true),
      (actionState.value === 'context')
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("p", { innerHTML: initialMessage.value }, null, 8, _hoisted_6)
            ])
          ]))
        : _createCommentVNode("", true),
      (summaryStatus.value === 'error')
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("strong", null, _toDisplayString(_unref(t)("aiAssistant.error.title")), 1),
              _cache[0] || (_cache[0] = _createTextVNode()),
              _createElementVNode("span", {
                textContent: _toDisplayString(summaryErrorMessage.value)
              }, null, 8, _hoisted_9),
              _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1)),
              _createVNode(_component_w_button, {
                style: {"display":"none"},
                onClick: restartSession,
                "leading-icon": "repeat",
                class: "restart-button",
                label: _unref(t)('aiAssistant.error.restartButton'),
                variant: "primary"
              }, null, 8, ["label"])
            ])
          ]))
        : _createCommentVNode("", true),
      (summaryStatus.value === 'ready' && actionState.value === 'summary')
        ? (_openBlock(), _createBlock(SummaryContent, {
            key: 5,
            "initial-context": __props.initialContext,
            "action-state": actionState.value,
            "review-status": reviewStatus.value,
            "summary-text": summaryText.value,
            "session-timestamp": _unref(sessionTimestamp),
            "onUpdate:summaryText": onSummaryTextUpdate
          }, null, 8, ["initial-context", "action-state", "review-status", "summary-text", "session-timestamp"]))
        : _createCommentVNode("", true)
    ]),
    (summaryStatus.value === 'ready' && actionState.value === 'summary')
      ? (_openBlock(), _createBlock(SummaryFooter, {
          key: 1,
          "summary-status": summaryStatus.value,
          "action-state": actionState.value,
          "initial-context": __props.initialContext,
          "custom-forms": _unref(customForms),
          "preemium-config": preemiumConfig,
          "summary-text": summaryText.value,
          "review-status": reviewStatus.value,
          onSummarySaved: onSummarySaved,
          onSummaryDenied: onSummaryDenied,
          onClose: onClose
        }, null, 8, ["summary-status", "action-state", "initial-context", "custom-forms", "summary-text", "review-status"]))
      : _createCommentVNode("", true)
  ]))
}
}

})